// React
import { useCallback, useState, useEffect, useRef } from "react";

// Resources
import { getAvailableAppointmentsCount } from "API/Resource/availableAppointmentsCount";

// Components
import { Box, Skeleton, Stack, Tooltip, Typography } from "@mui/material";
import { MBSChip } from "../../Common/MBSChip";

// Constants
import {
  GRACE_PERIOD,
  LOW_THRESHOLD,
  REFRESH_TIME,
} from "Constants/AppointmentStatusDisplayConstants";
import {
  MANITOBA_TIMEZONE,
  ONE_MINUTE,
  MINUTES_IN_HOUR,
} from "Constants/TimeConstants";

// Style
import { styles } from "./styles";

// Libraries
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";

/**
 * @brief Gets values for the frontend to display from the data sent from the backend.
 * @param appointmentsCount Object representing a count of appointments sent from the backend.
 * @param t i18next translation function
 * @returns { belowThreshold, countText, tooltipText } Object that tells the component if we're below threshold, what the text in the chip should say, and what the tool tip text is.
 */
const getDisplayValues = (appointmentsCount, t) => {
  let totalCount = 0;
  const lines = [];

  Object.entries(appointmentsCount)
    .sort(
      ([locationA, countA], [locationB, countB]) =>
        countB - countA || locationA.localeCompare(locationB)
    )
    .forEach(([location, count]) => {
      totalCount += count;
      lines.push(`${location} - ${count}`);
    });
  const tooltipText = lines.join("\n");

  let belowThreshold, countText;

  if (totalCount === 0) {
    belowThreshold = true;
    countText = t("mbsAppointmentsCount.noOpenSlots");
  } else {
    belowThreshold = totalCount < LOW_THRESHOLD;
    countText =
      (belowThreshold ? `< ${LOW_THRESHOLD}` : totalCount) +
      ` ${t("mbsAppointmentsCount.openSlots")}`;
  }

  return { belowThreshold, countText, tooltipText };
};

/**
 * @brief Gets the minutes passed since a given time
 * @param {DateTime} time The DateTime to check the time since. If negative, the function returns this parameter
 * @returns Minutes since the given time has passed
 */
const minutesAgoSince = (time) =>
  time >= 0
    ? Math.floor(
        DateTime.now().setZone(MANITOBA_TIMEZONE).diff(time, "minutes").minutes
      )
    : time;

export const AppointmentsStatusDisplay = () => {
  const { t } = useTranslation();

  const [appointmentsByLocation, setAppointmentsByLocation] = useState({});
  const [minutesSinceUpdate, setMinutesSinceUpdate] = useState(0);
  const [isUpdating, setIsUpdating] = useState(false);

  const ranInitialGet = useRef(false);
  const lastCacheUpdate = useRef(DateTime.fromMillis(0));
  const retryGet = useRef(false);
  const updateTimerId = useRef(null);

  const { belowThreshold, countText, tooltipText } = getDisplayValues(
    appointmentsByLocation,
    t
  );

  const updateCache = useCallback(async () => {
    const resetStatus = () => {
      setMinutesSinceUpdate(minutesAgoSince(lastCacheUpdate.current));
      setAppointmentsByLocation({});
    };

    setIsUpdating(true);

    try {
      const locationAvailabilities = await getAvailableAppointmentsCount();

      if (locationAvailabilities.length === 0) {
        lastCacheUpdate.current = DateTime.now().setZone(MANITOBA_TIMEZONE);
        resetStatus();
        setIsUpdating(false);
        return;
      }

      let mostRecentUpdate = DateTime.fromMillis(0);

      setAppointmentsByLocation(
        locationAvailabilities.reduce((availabilities, current) => {
          let updatedAt = DateTime.fromISO(current.updatedAt).setZone(
            MANITOBA_TIMEZONE
          );

          if (mostRecentUpdate < updatedAt) {
            mostRecentUpdate = updatedAt;
          }
          availabilities[current.location] = current.count;

          return availabilities;
        }, {})
      );

      // check for stale data
      const isStaleData = lastCacheUpdate.current >= mostRecentUpdate;

      if (isStaleData && !retryGet.current) {
        retryGet.current = true;
        updateTimerId.current = setTimeout(updateCache, GRACE_PERIOD);
      } else {
        let delay;
        if (retryGet.current) {
          retryGet.current = false;
          delay = REFRESH_TIME;
        } else {
          delay = Math.max(
            GRACE_PERIOD,
            REFRESH_TIME -
              DateTime.now()
                .setZone(MANITOBA_TIMEZONE)
                .diff(mostRecentUpdate, "milliseconds").milliseconds +
              GRACE_PERIOD
          );
        }
        updateTimerId.current = setTimeout(updateCache, delay);

        lastCacheUpdate.current = mostRecentUpdate;
        setMinutesSinceUpdate(minutesAgoSince(mostRecentUpdate));
      }
    } catch (error) {
      lastCacheUpdate.current = -1;
      resetStatus();
      updateTimerId.current = setTimeout(updateCache, REFRESH_TIME);
    }

    setIsUpdating(false);
  }, []);

  useEffect(() => {
    // update on component load (guard for react strict mode, it'll trigger the retry logic on the second run)
    if (!ranInitialGet.current) {
      ranInitialGet.current = true;
      updateCache();
    }

    return () => {
      clearTimeout(updateTimerId.current);
    };
  }, [updateCache]);

  useEffect(() => {
    const timeUpdatedIntervalId = setInterval(() => {
      setMinutesSinceUpdate(minutesAgoSince(lastCacheUpdate.current));
    }, ONE_MINUTE);

    return () => {
      clearInterval(timeUpdatedIntervalId);
    };
  }, []);

  const formatTimeSinceUpdate = (minutes) => {
    if (minutes < 0) {
      return t("mbsAppointmentsCount.failedToUpdate");
    }

    let timeSinceUpdate = `${t("mbsAppointmentsCount.updated")} `;

    if (minutes === 0) {
      timeSinceUpdate += t("mbsAppointmentsCount.now");
    } else if (minutes < MINUTES_IN_HOUR) {
      timeSinceUpdate +=
        minutes +
        (minutes > 1
          ? ` ${t("mbsAppointmentsCount.minutesAgo")}`
          : ` ${t("mbsAppointmentsCount.minuteAgo")}`);
    } else {
      timeSinceUpdate +=
        Math.floor(minutes / MINUTES_IN_HOUR) +
        (minutes >= 2 * MINUTES_IN_HOUR
          ? ` ${t("mbsAppointmentsCount.hoursAgo")}`
          : ` ${t("mbsAppointmentsCount.hourAgo")}`);
    }

    return timeSinceUpdate;
  };

  return (
    <Stack sx={styles.statusStack}>
      <Tooltip
        title={
          !isUpdating &&
          tooltipText.length > 0 && (
            <span style={{ whiteSpace: "pre-line" }}>{tooltipText}</span>
          )
        }
        slotProps={styles.tooltipSlotProps}
        arrow
      >
        <Box component="span" sx={{ display: "inline-flex" }}>
          <MBSChip
            variant={belowThreshold ? "secondary" : "primary"}
            sx={{ py: 0.75, px: 1.25 }}
          >
            <Typography
              variant="body2"
              color="white"
              align="center"
              sx={isUpdating ? styles.statusTextHidden : {}}
            >
              {countText}
            </Typography>
            {isUpdating && (
              <Skeleton
                variant="text"
                sx={styles.statusSkeleton}
                animation="wave"
              />
            )}
          </MBSChip>
        </Box>
      </Tooltip>
      <Typography variant="caption" color="text.secondary" align="center">
        {isUpdating
          ? t("mbsAppointmentsCount.updating")
          : formatTimeSinceUpdate(minutesSinceUpdate)}
      </Typography>
    </Stack>
  );
};
